import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { submitInterest } from '../utils/api';
import { toast } from 'sonner';
import NavigationBar from '@/components/NavigationBar';
import Footer from '@/components/Footer';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';

const RegisterInterest = ({ user }) => {
  const navigate = useNavigate();
  const [section, setSection] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+91',
    mobile: '',
    state: '',
    city: '',
    assetCategory: 'Luxury Cars',
    brand: '',
    otherBrand: '',
    model: '',
    otherModel: '',
    experiencePreferences: [],
    curationPackages: [],
    annualUsage: '',
    excitementReasons: []
  });

  // Load section 1 data from sessionStorage if available
  useEffect(() => {
    const section1Data = sessionStorage.getItem('section1Data');
    if (section1Data) {
      const data = JSON.parse(section1Data);
      setFormData(prev => ({ ...prev, ...data }));
      setSection(2);
      sessionStorage.removeItem('section1Data');
    }
  }, []);

  // Auto-fill user data if logged in
  useEffect(() => {
    if (user) {
      // Auto-fill from user profile
      const nameParts = user.name ? user.name.split(' ') : [];
      setFormData(prev => ({
        ...prev,
        firstName: user.first_name || nameParts[0] || '',
        lastName: user.last_name || nameParts.slice(1).join(' ') || '',
        email: user.email || '',
        countryCode: user.country_code || '+91',
        mobile: user.phone ? user.phone.replace(user.country_code || '+91', '') : '',
        state: user.state || '',
        city: user.city || ''
      }));
    }
  }, [user]);

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
    'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep'
  ];

  const luxuryBrands = {
    'Lamborghini': ['Urus', 'Revuelto', 'Huracán', 'Other'],
    'Ferrari': ['Roma', 'Portofino', 'SF90 Stradale', '812 Superfast', 'Purosangue', 'Other'],
    'Rolls-Royce': ['Phantom', 'Ghost', 'Cullinan', 'Spectre', 'Other'],
    'Bentley': ['Continental GT', 'Flying Spur', 'Bentayga', 'Other'],
    'Porsche': ['911', '718', 'Taycan', 'Panamera', 'Cayenne', 'Macan', 'Other'],
    'Aston Martin': ['DB12', 'Vantage', 'DBX', 'Other'],
    'McLaren': ['720S', 'Artura', 'GT', 'Other'],
    'Mercedes-Benz': ['S-Class', 'E-Class', 'G-Class', 'Maybach S-Class', 'AMG GT', 'GLS', 'GLE', 'Other'],
    'BMW': ['7 Series', '5 Series', 'X7', 'X5', 'M8', 'i7', 'iX', 'Other'],
    'Audi': ['A8', 'A6', 'Q8', 'Q7', 'e-tron GT', 'RS7', 'Other'],
    'Maserati': ['Quattroporte', 'Ghibli', 'Levante', 'Grecale', 'MC20', 'Other'],
    'Jaguar': ['F-Type', 'F-Pace', 'I-Pace', 'Other'],
    'Range Rover': ['Range Rover', 'Range Rover Sport', 'Range Rover Velar', 'Range Rover Evoque', 'Defender', 'Other']
  };

  const excitementOptions = [
    'High Assets: Ownership of high value assets through Fractional Ownership',
    'High Luxury: Excited to join India\'s first automotive themed exclusive lounge, cafe, and club',
    'High Lifestyle: Experiencing unique events, meets, experiences, and tours'
  ];

  const experienceOptions = [
    'Everyday Use',
    'Occasional Use in the city',
    'Outstation travel',
    'Weekend joy rides',
    'For use when travelling in another city',
    'To experience & enjoy different luxury cars'
  ];

  const curationPackageOptions = [
    'Urban Pack - Starting ₹1cr',
    'Grand Pack - Starting ₹2cr',
    'Exotique Pack - Starting ₹3cr'
  ];

  const usageOptions = [
    '1 month', '2 months', '3 months', '4 months', '5 months', '6 months', 'More than 6 months'
  ];

  const handleExperienceToggle = (option) => {
    const current = formData.experiencePreferences;
    if (current.includes(option)) {
      setFormData({
        ...formData,
        experiencePreferences: current.filter(item => item !== option)
      });
    } else {
      setFormData({
        ...formData,
        experiencePreferences: [...current, option]
      });
    }
  };

  const handleCurationPackageToggle = (option) => {
    const current = formData.curationPackages;
    if (current.includes(option)) {
      setFormData({
        ...formData,
        curationPackages: current.filter(item => item !== option)
      });
    } else {
      setFormData({
        ...formData,
        curationPackages: [...current, option]
      });
    }
  };

  const handleExcitementToggle = (option) => {
    const current = formData.excitementReasons;
    if (current.includes(option)) {
      setFormData({
        ...formData,
        excitementReasons: current.filter(item => item !== option)
      });
    } else {
      setFormData({
        ...formData,
        excitementReasons: [...current, option]
      });
    }
  };

  const validateSection1 = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.countryCode || !formData.mobile || !formData.state || !formData.city) {
      toast.error('Please fill all fields in Section 1');
      return false;
    }
    return true;
  };

  const handleContinue = () => {
    if (validateSection1()) {
      setSection(2);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Updated validation for brand and model
    const brandValid = formData.brand && (formData.brand !== 'Other' || formData.otherBrand);
    const modelValid = (formData.brand === 'Other') || formData.model;
    
    if (!brandValid || !modelValid || formData.experiencePreferences.length === 0 || !formData.annualUsage || formData.excitementReasons.length === 0) {
      toast.error('Please complete all fields in Section 2');
      return;
    }

    if (formData.brand === 'Other' && !formData.otherBrand) {
      toast.error('Please specify the brand name');
      return;
    }

    if (formData.model === 'Other' && !formData.otherModel) {
      toast.error('Please specify the model name');
      return;
    }

    setLoading(true);
    try {
      // Prepare complete form data
      const completeFormData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        countryCode: formData.countryCode,
        mobile: formData.mobile,
        state: formData.state,
        city: formData.city,
        assetCategory: formData.assetCategory,
        brand: formData.brand === 'Other' ? formData.otherBrand : formData.brand,
        otherBrand: formData.otherBrand,
        model: formData.brand === 'Other' ? 'Not Applicable' : (formData.model === 'Other' ? formData.otherModel : formData.model),
        otherModel: formData.otherModel,
        experiencePreferences: formData.experiencePreferences,
        curationPackages: formData.curationPackages,
        annualUsage: formData.annualUsage,
        excitementReasons: formData.excitementReasons
      };

      await submitInterest({
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: `${formData.countryCode}${formData.mobile}`,
        interest_type: 'vehicle',
        message: JSON.stringify(completeFormData)
      });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FFFBF5' }}>
        <NavigationBar user={user} />
        
        <div className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 md:p-12">
              <div className="mb-8">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#E6E6FA' }}>
                  <Check size={40} style={{ color: '#6B46C1' }} />
                </div>
                <h1 className="text-4xl font-bold mb-4">Thank You for Expressing Your Interest</h1>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Your preferences have been received.
                </p>
              </div>
              
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                Our concierge team will personally connect with you soon to curate your Haute Circle experience — designed around your passions, lifestyle, and the art of true luxury.
              </p>
              
              <Link to="/">
                <Button className="bg-black text-white hover:bg-black/90 px-8 py-3 text-base">
                  Return to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFBF5' }}>
      <NavigationBar user={user} />
      
      <div className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Button 
            onClick={() => navigate(-1)}
            className="mb-6 sm:mb-8 border border-black bg-white text-black hover:bg-black hover:text-white transition-all text-sm sm:text-base"
          >
            ← Back
          </Button>
          
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">Express Your Interest</h1>
            <div className="flex items-center justify-center gap-2 sm:gap-3 mt-4 sm:mt-6">
              <div className={`flex items-center ${section >= 1 ? 'text-gold' : 'text-gray-400'}`}>
                <div className={`min-w-[44px] min-h-[44px] sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${section >= 1 ? 'bg-gold text-white' : 'bg-gray-200'} font-semibold text-sm sm:text-base`}>
                  1
                </div>
                <span className="ml-2 sm:ml-3 text-xs sm:text-sm font-medium">About You</span>
              </div>
              <ChevronRight size={16} className="text-gray-400 sm:w-5 sm:h-5" />
              <div className={`flex items-center ${section >= 2 ? 'text-gold' : 'text-gray-400'}`}>
                <div className={`min-w-[44px] min-h-[44px] sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${section >= 2 ? 'bg-gold text-white' : 'bg-gray-200'} font-semibold text-sm sm:text-base`}>
                  2
                </div>
                <span className="ml-2 sm:ml-3 text-xs sm:text-sm font-medium">Your Interests</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
            {section === 1 && (
              <div>
                <h2 className="text-2xl font-bold mb-3">Section 1: About You</h2>
                <p className="text-gray-600 mb-8">We'd love to get to know you before welcoming you to The Haute Circle.</p>
                
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">First Name*</label>
                      <Input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Last Name*</label>
                      <Input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email Address*</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Mobile Number*</label>
                    <div className="flex gap-2">
                      <select
                        value={formData.countryCode}
                        onChange={(e) => setFormData({...formData, countryCode: e.target.value})}
                        className="w-32 px-3 py-3 sm:py-2 border border-gray-300 rounded-lg min-h-[44px]"
                        required
                      >
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+971">🇦🇪 +971</option>
                        <option value="+65">🇸🇬 +65</option>
                        <option value="+61">🇦🇺 +61</option>
                      </select>
                      <Input
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                        placeholder="Enter mobile number"
                        className="flex-1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">State*</label>
                    <select
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      className="w-full px-4 py-3 sm:py-2 border border-gray-300 rounded-lg min-h-[44px]"
                      required
                    >
                      <option value="">Select your state</option>
                      {indianStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">City*</label>
                    <Input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="Enter your city"
                      required
                    />
                  </div>

                  <Button
                    onClick={handleContinue}
                    className="w-full bg-white text-gold hover:bg-white/90 font-semibold py-3 flex items-center justify-center gap-2 shadow-lg min-h-[44px]"
                  >
                    Continue
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            )}

            {section === 2 && (
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <button
                    type="button"
                    onClick={() => setSection(1)}
                    className="flex items-center text-gray-600 hover:text-black mb-4"
                  >
                    <ChevronLeft size={20} />
                    <span className="ml-1">Back</span>
                  </button>
                  <h2 className="text-2xl font-bold mb-3">Section 2: Your Interests</h2>
                  <p className="text-gray-600 mb-8">Tell us what inspires your interest in luxury ownership and the experiences you'd like to explore.</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Preferred Asset Category*</label>
                    <select
                      value={formData.assetCategory}
                      onChange={(e) => setFormData({...formData, assetCategory: e.target.value})}
                      className="w-full px-4 py-3 sm:py-2 border border-gray-300 rounded-lg min-h-[44px]"
                      required
                    >
                      <option value="Luxury Cars">Luxury Cars</option>
                      <option value="Vacation Homes" disabled>Vacation Homes (Coming Soon)</option>
                      <option value="Private Aircraft" disabled>Private Aircraft (Coming Soon)</option>
                      <option value="Luxury Yachts" disabled>Luxury Yachts (Coming Soon)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Preferred Automobile Brand*</label>
                    <select
                      value={formData.brand}
                      onChange={(e) => setFormData({...formData, brand: e.target.value, model: '', otherBrand: ''})}
                      className="w-full px-4 py-3 sm:py-2 border border-gray-300 rounded-lg min-h-[44px]"
                      required
                    >
                      <option value="">Select a brand</option>
                      {Object.keys(luxuryBrands).map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                    {formData.brand === 'Other' && (
                      <div className="mt-3">
                        <Input
                          type="text"
                          value={formData.otherBrand}
                          onChange={(e) => setFormData({...formData, otherBrand: e.target.value})}
                          placeholder="Please specify the brand"
                          required
                        />
                      </div>
                    )}
                  </div>

                  {formData.brand && formData.brand !== 'Other' && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Preferred Model*</label>
                      <select
                        value={formData.model}
                        onChange={(e) => setFormData({...formData, model: e.target.value, otherModel: ''})}
                        className="w-full px-4 py-3 sm:py-2 border border-gray-300 rounded-lg min-h-[44px]"
                        required
                      >
                        <option value="">Select a model</option>
                        {luxuryBrands[formData.brand].map(model => (
                          <option key={model} value={model}>{model}</option>
                        ))}
                      </select>
                      {formData.model === 'Other' && (
                        <div className="mt-3">
                          <Input
                            type="text"
                            value={formData.otherModel}
                            onChange={(e) => setFormData({...formData, otherModel: e.target.value})}
                            placeholder="Please specify the model"
                            required
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {formData.brand === 'Other' && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Preferred Model*</label>
                      <Input
                        type="text"
                        value={formData.otherModel}
                        onChange={(e) => setFormData({...formData, otherModel: e.target.value})}
                        placeholder="Please specify the model"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-3">How would you like to experience your car?* (Select one or more)</label>
                    <div className="space-y-3">
                      {experienceOptions.map(option => (
                        <label key={option} className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.experiencePreferences.includes(option)}
                            onChange={() => handleExperienceToggle(option)}
                            className="w-5 h-5 text-gold border-gray-300 rounded focus:ring-gold"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Estimated Annual Usage*</label>
                    <p className="text-sm text-gray-600 mb-3">Annual usage refers to the total number of days you plan to use the asset in a year, which can be spread across any months or dates of your choice.</p>
                    <select
                      value={formData.annualUsage}
                      onChange={(e) => setFormData({...formData, annualUsage: e.target.value})}
                      className="w-full px-4 py-3 sm:py-2 border border-gray-300 rounded-lg min-h-[44px]"
                      required
                    >
                      <option value="">Select usage duration</option>
                      {usageOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-3">Would you consider any of the 360 day packages? (Select one or more)</label>
                    <div className="space-y-3">
                      {curationPackageOptions.map(option => (
                        <label key={option} className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.curationPackages.includes(option)}
                            onChange={() => handleCurationPackageToggle(option)}
                            className="w-5 h-5 text-gold border-gray-300 rounded focus:ring-gold"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-3">What excites you most about The Haute Circle?* (Select one or more)</label>
                    <div className="space-y-3">
                      {excitementOptions.map(option => (
                        <label key={option} className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.excitementReasons.includes(option)}
                            onChange={() => handleExcitementToggle(option)}
                            className="w-5 h-5 text-gold border-gray-300 rounded focus:ring-gold mt-0.5"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-black text-white hover:bg-black/90 font-semibold py-3"
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Submit'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default RegisterInterest;
